import { React, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import About from "./about";
import Settings from "./settings";

import { TopBar, SideBar } from "../../layout";
import Modal from "../../modal/Modal";
import config from "../../../config";
import { login } from "../../../features/authentication";
import { unsetContext } from "../../../features/context";

import encryption from "../../../lib/encryption";

import { CommunityMembership } from "../../widgets";

import axiosCall from "../../../lib/axios";

const Context = ({ language, signout, pageRefs, pageFunctions }) => {
  ///////////////// INITIALIZE /////////////////
  const navigate = useNavigate();
  const auth = useSelector((state) => state.authentication.value);
  const context = useSelector((state) => state.context.value);
  const dispatch = useDispatch();

  const [communities, setCommunities] = useState(0);
  const [level, setLevel] = useState(0);
  const [qty, setQty] = useState(0);

  const [editCommunity, setEditCommunity] = useState(false);
  const [editLicense, setEditLicense] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [confirmClosure, setConfirmClosure] = useState(false);

  const [closeButton, setCloseButton] = useState("");
  const [closeLabel, setCloseLabel] = useState("");
  const [closeWarning, setCloseWarning] = useState("");
  const [closeConfirm, setCloseConfirm] = useState("");
  const [closeNow, setCloseNow] = useState("");
  const [quitCommunity, setQuitCommunity] = useState(false);

  const initialize = async () => {
    try {
      if (context.license_qty) {
        try {
          let q = new Intl.NumberFormat(language.locale).format(
            context.license_qty
          );
          setQty(q);
        } catch (e) {
          setQty(context.license_qty);
        }
      }
      setLevel(config.levels.indexOf(auth.role));

      if (config.levels.indexOf(auth.role) > 3) {
        //owner - close community
        setCloseButton(language.labels.communities.close_community);
        setCloseLabel(language.labels.communities.close);
        setCloseWarning(language.labels.communities.close_warning);
        setCloseConfirm(language.labels.app.consequences_understood);
        setCloseNow(language.labels.communities.close_now);
        setQuitCommunity(true);
      } else {
        //not owner - leave community
        setCloseButton(language.labels.communities.leave_community);
        setCloseLabel(language.labels.communities.leave);
        setCloseWarning(language.labels.communities.leave_warning);
        setCloseConfirm(language.labels.app.yes_sure);
        setCloseNow(language.labels.communities.leave_now);
        setQuitCommunity(false);
      }
    } catch (err) {
      signout();
    }
  };

  useEffect(() => {
    if (
      auth.agree_terms !== "x" &&
      auth.uid &&
      localStorage.getItem("communities")
    ) {
      let obj = JSON.parse(localStorage.getItem("communities"));
      setCommunities(obj.length);
      initialize();
    } else {
      signout();
    }
  }, []);

  ////////////// CLOSE & LEAVE COMMUNITY //////////////
  const quitCommunityNow = async (close) => {
    let api = "member/leave";
    if (close) api = "community/close";
    let result = await axiosCall(api, { cid: context._id });
    if (result.success && result.status === 200) {
      //update jwt
      localStorage.setItem("last_updated", Date.now());
      localStorage.setItem("tag", encryption.encrypt(result.data.jwt));

      //reset communities
      const _communities = await spliceCommunities();
      localStorage.setItem("communities", JSON.stringify(_communities));

      //set tag, client_id and last_update
      localStorage.setItem("last_updated", Date.now());
      const hash = encryption.encrypt(result.data.jwt);
      localStorage.setItem("tag", hash);

      //reset authorization
      let _auth = { ...auth };
      delete _auth.community_id;
      delete _auth.role;
      dispatch(login(_auth));

      //unset context
      dispatch(unsetContext());
      navigate("/hub");
    } else if (result.refresh) {
      //token has been refreshed, try again
      quitCommunityNow(close);
    } else {
      //refresh token expired or unknown error
      signout();
    }
  };

  async function spliceCommunities() {
    let _communities = JSON.parse(localStorage.getItem("communities"));
    for (var i = 0; i < _communities.length; i++) {
      if (_communities[i].id === context._id) {
        _communities.splice(i, 1);
        return _communities;
      }
    }
  }

  ///////////////// RENDER GUI /////////////////
  return (
    <div className="main" ref={pageRefs._main_}>
      <div
        className="sr-only"
        id="primary_focus"
        tabIndex={0}
        onBlur={(e) => e.target.setAttribute("tabIndex", -1)}
      ></div>
      <div
        ref={pageRefs.skip_link}
        className="link sr-only"
        role="link"
        onClick={() => pageFunctions.skipLink(false)}
        onKeyUpCapture={(e) =>
          e.key === "Enter" && pageRefs.main_focus.current.focus()
        }
        onFocus={() => pageFunctions.skipLink()}
        onBlur={() => pageFunctions.skipLink(false)}
        tabIndex={0}
      >
        {language.labels.aria.skip}
      </div>
      <TopBar language={language} toggleMenu={pageFunctions.toggleMenu} />
      <SideBar
        language={language}
        signout={signout}
        auth={auth}
        communities={communities}
        context={context}
        resetMenu={pageFunctions.resetMenu}
        ref={pageRefs.sidebar_ref}
      />
      <main className="main-page" ref={pageRefs.main_focus} tabIndex={0}>
        <div
          className="page-section"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div
            style={{ display: "flex", alignItems: "center", maxWidth: "44rem" }}
          >
            {/* Icon and Heading */}
            <div
              role="img"
              aria-label={language.labels.aria.logo.replace(
                /{org}/g,
                context.title
              )}
              className="menu-crest"
              style={{
                height: "3em",
                width: "3em",
                backgroundImage: `url("${
                  config.server.storage.bucket + context.crest
                }")`,
              }}
            ></div>
            <div style={{ width: "0.5em" }}></div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                flex: "1",
                width: "calc(100% - 2.5em)",
              }}
            >
              <h1 className="heading" style={{ width: "100%", padding: "0" }}>
                {context.uname}
              </h1>
              <div className="handle2" style={{ width: "100%" }}>
                {context.title}
              </div>
            </div>
            <div style={{ width: "0.5em" }}></div>
          </div>

          {/* role */}
          <div
            style={{ fontSize: "1.1em", padding: "0.5em 0" }}
            dangerouslySetInnerHTML={{
              __html: language.labels.role.replace(
                /{role}/g,
                language.labels.roles[auth.role]
              ),
            }}
          />
          {level === 3 && (
            <div style={{ marginTop: "0.25em" }}>
              {language.labels.communities.editor.replace(
                /{role}/g,
                language.labels.roles[context.role]
              )}
            </div>
          )}
          {level === 4 && (
            <div style={{ marginTop: "0.25em" }}>
              {language.labels.communities.owner}
            </div>
          )}

          {/* verified */}
          {context.verified && (
            <div
              style={{
                display: "flex",
                gap: "0.5em",
                marginTop: "0.5em",
              }}
            >
              <span className="glyphs">*</span>
              <span style={{ opacity: 0.8, fontStyle: "italic" }}>
                {language.labels.communities.headings.verified}
              </span>
            </div>
          )}

          {/* privacy */}
          <div
            style={{
              display: "flex",
              gap: "0.5em",
              marginTop: "0.5em",
            }}
          >
            <span className="glyphs">{context.private ? "L" : "K"}</span>
            <span style={{ opacity: 0.8, fontStyle: "italic" }}>
              {context.private
                ? language.labels.communities.settings.private
                : language.labels.communities.settings.public}
            </span>
          </div>
        </div>

        {/********** SETTINGS ***********/}
        {!editMode && level > 2 && (
          <Settings
            language={language}
            signout={signout}
            props={{
              level,
              community: context,
              editCommunity,
              setEditCommunity,
              editLicense,
              setEditLicense,
              qty,
            }}
          />
        )}

        {/* retire community */}
        {!editMode && !editCommunity && !editLicense && level > 3 && (
          <div className="page-section">
            <button
              className="button alert"
              style={{ fontSize: "0.9em" }}
              onClick={() => setShowModal(true)}
            >
              {closeButton}
            </button>
          </div>
        )}

        {/********** MEMBERSHIP ***********/}
        {!editMode && !editCommunity && !editLicense && (
          <CommunityMembership
            language={language}
            community={context}
            level={level}
            navigate={navigate}
            signout={signout}
          />
        )}

        {/********** ABOUT ***********/}
        {!editCommunity && !editLicense && (
          <About
            language={language}
            signout={signout}
            main_focus={pageRefs.main_focus}
            props={{
              level,
              role: auth.role,
              community: context,
              editMode,
              setEditMode,
            }}
          />
        )}

        {/* quit community */}
        {!editMode && level < 4 && (
          <div className="page-section" style={{ paddingBottom: "2em" }}>
            <button
              className="button alert"
              style={{ fontSize: "0.9em", margin: "2em 0" }}
              onClick={() => setShowModal(true)}
            >
              {closeButton}
            </button>
          </div>
        )}
      </main>

      {/* Leave/Close Modal */}
      <Modal
        className="modal-dynamic"
        title={closeLabel.replace(/{org}/g, context.title)}
        show={showModal}
      >
        <div
          style={{ fontSize: "1.1em" }}
          dangerouslySetInnerHTML={{
            __html: closeWarning.replace(/{org}/g, context.title),
          }}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            fontSize: ".9rem",
            marginTop: "1rem",
          }}
        >
          <input
            id="consequence_understood"
            type={"checkbox"}
            onChange={(e) => setConfirmClosure(e.target.checked)}
            defaultChecked={confirmClosure}
          />
          <div style={{ minWidth: "0.5rem" }}></div>
          <label className="hover" htmlFor="consequence_understood">
            {closeConfirm}
          </label>
        </div>
        {confirmClosure && (
          <div style={{ display: "flex", marginTop: "0.5em" }}>
            <button
              className="button"
              onClick={() => quitCommunityNow(quitCommunity)}
            >
              {closeNow}
            </button>
          </div>
        )}
        <div style={{ display: "flex", marginTop: "2em" }}>
          <button className="button-off" onClick={() => setShowModal(false)}>
            {language.labels.app.cancel}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Context;
